import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { IconName } from '@databox/icons/dist/types';

import type { APIData, PopupListItem, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

export const enum BaseAction {
	GET_ASSISTANCE = 'getAssistance',
	DUPLICATE = 'duplicate',
	DELETE = 'delete',
}

export interface ActionListItem<T> extends PopupListItem<T> {
	action: T;
	icon: IconName;
}

export interface Args {
	duplicate: () => void;
	delete: () => void;
}

export const MAX_OPTIONS_SHOWN_ON_PAGE = 13;

export default abstract class CustomMetricsEditActionsBaseComponent<
	T extends Args,
> extends Component<T> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	abstract origin: string;

	abstract get sourceTypes(): string[];

	abstract get options(): ActionListItem<string>[];

	abstract get showDuplicate(): boolean;
	abstract get showDelete(): boolean;

	getAssistanceAction: ActionListItem<BaseAction> = {
		icon: 'Assistance',
		text: 'Get assistance',
		action: BaseAction.GET_ASSISTANCE,
	};

	duplicateAction: ActionListItem<BaseAction> = {
		icon: 'Copy',
		text: 'Duplicate',
		action: BaseAction.DUPLICATE,
	};

	deleteAction: ActionListItem<BaseAction> = {
		icon: 'Trash',
		text: 'Delete',
		class: 'delete',
		action: BaseAction.DELETE,
	};

	get showGetAssistance() {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	@action
	doAction(actionName?: string) {
		if (actionName) {
			this[actionName as BaseAction]();
		}
	}

	@action
	getAssistance(): void {
		const trackingData: APIData.Tracking.Data = {
			origin: this.origin,
			sourceTypes: this.sourceTypes,
		};

		this.analyticsService.event(TrackingEvent.CALL_INITIATED, trackingData);
		this.accountService.requestSetupHelpAlternative();
	}

	@action
	duplicate() {
		this.args.duplicate?.();
	}

	@action
	delete() {
		this.args.delete?.();
	}
}
